import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCountry } from "@/contexts/CountryContext";
import { useExpenseValidation } from "@/hooks/useExpenseValidation";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import ExpenseCategorySelector from "./ExpenseCategorySelector";
import { CurrencyInput } from "@/components/i18n/Currency";
import expenseService from "@/services/expenseService";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  ExpenseCategory,
  CreateExpenseRequest,
  ExpenseContract,
} from "@/types/expense";

// Form data type is now derived from the country-specific schema
type ExpenseFormData = {
  contractorContractId: string;
  amount: string;
  description: string;
  expenseDate: string;
  category: ExpenseCategory;
  receipt?: FileList;
};

interface CreateExpenseFormProps {
  onSuccess?: (expense: any) => void;
  onCancel?: () => void;
  availableContracts?: ExpenseContract[];
  className?: string;
}

const CreateExpenseForm: React.FC<CreateExpenseFormProps> = ({
  onSuccess,
  onCancel,
  availableContracts = [],
  className,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [createdExpense, setCreatedExpense] = useState<any>(null);
  const [isSubmittingForApproval, setIsSubmittingForApproval] = useState(false);
  const { toast } = useToast();
  const { getCurrencySymbol } = useCountry();
  const { 
    createExpenseSchema, 
    getValidationMessage, 
    isReceiptRequired, 
    getMaxAmount,
    getAllowedCategories 
  } = useExpenseValidation();

  const form = useForm<ExpenseFormData>({
    resolver: zodResolver(createExpenseSchema),
    defaultValues: {
      contractorContractId: "",
      amount: "",
      description: "",
      expenseDate: new Date().toISOString().split("T")[0], // Today's date
      category: undefined,
      receipt: undefined,
    },
  });

  const onSubmit = async (data: ExpenseFormData) => {
    setIsSubmitting(true);
    setIsUploading(true);

    try {
      let receiptUrl: string | undefined;
      let receiptFilename: string | undefined;

      // Handle file upload if a receipt is provided
      if (data.receipt && data.receipt.length > 0) {
        const file = data.receipt[0];
        const fileType = file.type;
        receiptFilename = file.name;

        console.log("Uploading receipt file:", receiptFilename, "Type:", fileType);

        // Step 1: Get a presigned URL for the file upload
        interface UploadUrlResponse {
          uploadUrl: string;
          fileKey: string;
          s3Url: string;
        }

        const uploadUrlResponse = await ApiService.request<UploadUrlResponse>(
          EndpointKeys.CONTRACTOR_EXPENSE_RECEIPT_UPLOAD_URL,
          {
            method: "POST",
            body: { fileType },
          }
        );

        // Step 2: Upload the file directly to S3
        try {
          console.log("Starting S3 upload to:", uploadUrlResponse.uploadUrl);

          const uploadResponse = await fetch(uploadUrlResponse.uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": fileType,
            },
            mode: "cors",
            credentials: "omit",
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error(
              `S3 upload failed with status: ${uploadResponse.status} ${uploadResponse.statusText}`
            );
            console.error("Error details:", errorText);
            throw new Error(
              `File upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
            );
          }

          receiptUrl = uploadUrlResponse.s3Url;
          console.log("Upload successful, S3 URL:", receiptUrl);
        } catch (err) {
          console.error("Error during S3 upload:", err);
          throw err;
        }
      }

      setIsUploading(false);

      // Step 3: Create the expense with the receipt URL
      const requestData: CreateExpenseRequest = {
        contractorContractId: data.contractorContractId,
        amount: data.amount,
        description: data.description.trim(),
        expenseDate: data.expenseDate,
        category: data.category,
        receiptUrl: receiptUrl,
        receiptFilename: receiptFilename,
      };

      const result = await expenseService.createExpense(requestData);

      toast({
        title: "Success",
        description:
          "Expense created successfully. Please submit it for approval.",
      });

      // Store the created expense
      setCreatedExpense(result.expense);

      // Don't reset form or call success callback yet - wait for submission
    } catch (error: any) {
      console.error("Error creating expense:", error);

      // Handle different types of errors with specific messaging
      let title = "Error";
      let description = "Failed to create expense. Please try again.";
      
      // Handle ApiError objects with status codes and body
      if (error.status === 422) {
        // Handle 422 Unprocessable Entity - usually business logic errors
        if (error.body?.error) {
          description = error.body.error;

          // Set appropriate title based on error content
          if (error.body.error.includes("Contract must be approved")) {
            title = "Contract Not Approved";
          } else if (
            error.body.error.includes("Contract configuration incomplete")
          ) {
            title = "Contract Setup Incomplete";
          } else {
            title = "Unable to Create Expense";
          }

          // If there are additional details, append them
          if (error.body.details) {
            description += ` ${error.body.details}`;
          }
        } else if (error.message) {
          // Fallback to error message if body doesn't have error field
          description = error.message;
          if (error.message.includes("Contract must be approved")) {
            title = "Contract Not Approved";
          }
        }
      } else if (error.status === 400) {
        // Handle 400 Bad Request - validation errors
        title = "Invalid Request";
        description =
          error.body?.error ||
          error.message ||
          "Please check your input and try again.";
      } else if (error.status === 403) {
        // Handle 403 Forbidden - permission errors
        title = "Permission Denied";
        description =
          error.body?.error ||
          error.message ||
          "You do not have permission to perform this action.";
      } else if (error instanceof Error) {
        // Handle other Error objects
        if (error.message.includes("Contract must be approved")) {
          title = "Contract Not Approved";
          description =
            "This contract must be approved by your client before you can create expenses. Please contact your client to approve the contract.";
        } else if (
          error.message.includes("Contract configuration incomplete")
        ) {
          title = "Contract Setup Incomplete";
          description =
            "This contract needs to be properly configured. Please contact support for assistance.";
        } else if (error.message.includes("Missing required field")) {
          title = "Missing Information";
          description = error.message;
        } else if (error.message.includes("Amount must be greater than zero")) {
          title = "Invalid Amount";
          description = "Please enter a valid amount greater than zero.";
        } else if (
          error.message.includes("Expense date cannot be in the future")
        ) {
          title = "Invalid Date";
          description =
            "Expense date cannot be in the future. Please select today or an earlier date.";
        } else if (error.message.includes("Description is required")) {
          title = "Missing Description";
          description = "Please provide a description for this expense.";
        } else if (error.message.includes("File upload failed")) {
          title = "Upload Failed";
          description = "Failed to upload receipt. Please try again or create the expense without a receipt.";
        } else {
          // Use the error message if it's meaningful, otherwise use default
          description = error.message || description;
        }
      }

      toast({
        title,
        description,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!createdExpense) return;

    setIsSubmittingForApproval(true);
    try {
      await expenseService.submitExpense(createdExpense.id, {});
      
      toast({
        title: "Success",
        description: "Expense submitted for approval",
      });

      // Reset form
      form.reset();
      setCreatedExpense(null);

      // Call success callback
      onSuccess?.(createdExpense);
    } catch (error: any) {
      console.error("Error submitting expense:", error);
      toast({
        title: "Error",
        description: "Failed to submit expense for approval. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingForApproval(false);
    }
  };

  const formatAmount = (value: string) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }

    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + "." + parts[1].substring(0, 2);
    }

    return numericValue;
  };

  return (
    <div className={className}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Contract Selection */}
          <FormField
            control={form.control}
            name="contractorContractId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contract *</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full" data-testid="expense-contract-select">
                      <SelectValue
                        placeholder="Select a contract..."
                        className="text-foreground"
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    {availableContracts.map((contract) => (
                      <SelectItem
                        key={contract.id}
                        value={contract.id}
                        className="text-gray-900 hover:bg-blue-50 hover:text-blue-900 cursor-pointer py-2 px-3"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {contract.title}
                          </span>
                          <span className="text-sm text-gray-600">
                            {contract.clientName}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount ({getCurrencySymbol()}) *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                        {getCurrencySymbol()}
                      </span>
                      <Input
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => {
                          const formatted = formatAmount(e.target.value);
                          field.onChange(formatted);
                        }}
                        className="pl-8 text-right"
                        data-testid="expense-amount-input"
                      />
                    </div>
                  </FormControl>
                  <div className="text-xs text-muted-foreground mt-1">
                    Maximum: {getCurrencySymbol()}{getMaxAmount()}
                    {isReceiptRequired(field.value || "0") && (
                      <span className="ml-2 text-orange-600 font-medium">
                        • Receipt required
                      </span>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Expense Date */}
            <FormField
              control={form.control}
              name="expenseDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expense Date *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      max={new Date().toISOString().split("T")[0]}
                      data-testid="expense-date-input"
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground mt-1">
                    {getValidationMessage('expenseDate')}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Category */}
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category *</FormLabel>
                <FormControl>
                  <ExpenseCategorySelector
                    value={field.value}
                    onValueChange={field.onChange}
                    required
                    allowedCategories={getAllowedCategories()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe the expense..."
                    className="resize-none"
                    rows={3}
                    {...field}
                    data-testid="expense-description-input"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Receipt Upload */}
          <FormField
            control={form.control}
            name="receipt"
            render={({ field: { onChange, value, ...field } }) => (
              <FormItem>
                <FormLabel>Receipt (Optional)</FormLabel>
                <FormControl>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={(e) => onChange(e.target.files)}
                    {...field}
                    className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </FormControl>
                <p className="text-sm text-muted-foreground">
                  Upload a receipt image (JPEG, PNG) or PDF. Max size: 10MB
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            {!createdExpense ? (
              <>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="save-expense-button">
                  {isUploading
                    ? "Uploading receipt..."
                    : isSubmitting
                    ? "Creating..."
                    : "Save as Draft"}
                </Button>
                {onCancel && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button 
                  type="button"
                  onClick={handleSubmitForApproval}
                  disabled={isSubmittingForApproval}
                  className="flex-1"
                  data-testid="submit-expense-button"
                >
                  {isSubmittingForApproval ? "Submitting..." : "Submit for Approval"}
                </Button>
                {onCancel && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    className="flex-1"
                  >
                    Close
                  </Button>
                )}
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default CreateExpenseForm;
